import type {
  EditableComponentsMap,
  SelectedPromptOptionValues,
  HeaderContent,
  PromptOptionsForEditableComponents,
  PromptOptionValue,
  PromptSuggestionsValue,
} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {EditableComponent, PromptOption, RedesignIssueType, MoodType, PromptSuggestions} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {morphPoster} from '@Libraries/ai-text-design-morph.library';
import {initiatePosterMorphLoading, terminatePosterMorphLoading} from '@PosterWhiteboard/poster/poster-init';
import {AiRedesignPromptBoxIssue} from '@Libraries/ai-redesign-prompt-box-library';
import {refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import type {FeedbackReaction} from '@Components/feedback-toast/feedback-toast.types';
import type {AiRedesignPromptBoxPanel} from '@Panels/ai-redesign-prompt-box-panel/ai-redesign-prompt-box-panel.types';
import {isUserPremium} from '@Libraries/user.library';
import {openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';

export const POSTER_AI_REDESIGN_TEXT_GENERATION_COST = 1;
export const POSTER_AI_REDESIGN_IMAGE_GENERATION_COST = 3;

export const POSTER_AI_REDESIGN_PREVIOUS_PROMPT = 'poster-ai-redesign-previous-prompt';
export const POSTER_AI_REDESIGN_EDITABLE_COMPONENTS_KEY = 'poster-ai-redesign-editable-components-v1';

const DEFAULT_EDITABLE_COMPONENTS_MAP: EditableComponentsMap = {
  [EditableComponent.TEXT]: true,
  [EditableComponent.IMAGE]: false,
};

export const calculateAiRedesignCost = (editableComponentsMap: EditableComponentsMap): number => {
  let cost = 0;

  for (const [componentTypeStr, isEnabled] of Object.entries(editableComponentsMap)) {
    if (!isEnabled) continue;

    const componentType = componentTypeStr as EditableComponent;

    switch (componentType) {
      case EditableComponent.TEXT:
        cost += POSTER_AI_REDESIGN_TEXT_GENERATION_COST;
        break;
      case EditableComponent.IMAGE:
        cost += POSTER_AI_REDESIGN_IMAGE_GENERATION_COST;
        break;
      default:
        break;
    }
  }

  return cost;
};

/**
 * Checks if a poster has any text items with non-empty content
 * @param poster - The poster instance to check
 * @returns true if the poster has text items with content, false if all text items are empty or no text items exist
 */
export const doesPosterHaveTextItems = (poster: Poster): boolean => {
  const textItems = [];
  for (const [, page] of Object.entries(poster.pages.pagesHashMap)) {
    for (const [, item] of Object.entries(page.items.itemsHashMap)) {
      if (item.isFancyText() || item.isText()) {
        textItems.push(item);
      }
    }
  }

  return textItems.some((item) => {
    const textContent = item.text as string | undefined;
    return textContent && textContent.trim() !== '';
  });
};

async function checkIfUserHasCreditsForPosterAiRedesign(editableComponentsMap: EditableComponentsMap): Promise<void> {
  await window.PMW.readLocal('posterbuilder/checkUserHasCreditsForAiRedesign', {
    editableComponentsMap,
  });
}

const isTextOnlyEnabledInEditableComponentsMap = (editableComponentsMap: EditableComponentsMap): boolean => {
  return (editableComponentsMap[EditableComponent.TEXT] && Object.entries(editableComponentsMap).filter(([, isEnabled]) => isEnabled).length === 1) ?? false;
};

export const regenerateContent = async (
  prompt: string,
  selectedPromptOptionValues: SelectedPromptOptionValues,
  editableComponentsMap: EditableComponentsMap,
  remainingCredits: number
): Promise<void> => {
  const hashedId = window.posterEditor?.whiteboard?.hashedID;
  if (!hashedId) {
    throw new AiRedesignPromptBoxIssue(window.i18next.t('pmwjs_error_occurred_retry'), 'icon-exclamation-triangle', RedesignIssueType.ERROR);
  }

  try {
    await checkIfUserHasCreditsForPosterAiRedesign(editableComponentsMap);
  } catch (error: any) {
    if (error?.code === 'low_credits') {
      throw new AiRedesignPromptBoxIssue(window.i18next.t('pmwjs_insufficient_credits_for_ai_redesign'), 'icon-credits', RedesignIssueType.WARNING);
    }

    throw new AiRedesignPromptBoxIssue(window.i18next.t('pmwjs_error_occurred_retry'), 'icon-exclamation-triangle', RedesignIssueType.ERROR);
  }

  const cost = calculateAiRedesignCost(editableComponentsMap);
  if (remainingCredits < cost) {
    throw new AiRedesignPromptBoxIssue(window.i18next.t('pmwjs_insufficient_credits_for_ai_redesign'), 'icon-credits', RedesignIssueType.WARNING);
  }

  if (isTextOnlyEnabledInEditableComponentsMap(editableComponentsMap)) {
    const poster = window.posterEditor?.whiteboard;
    if (poster && !doesPosterHaveTextItems(poster)) {
      throw new AiRedesignPromptBoxIssue(window.i18next.t('pmwjs_cannot_rewrite_empty_text_items'), 'icon-exclamation-triangle', RedesignIssueType.ERROR);
    }
  }

  const loadingKey = `ai-redesign-${Date.now()}`;
  let morphId = undefined;

  try {
    initiatePosterMorphLoading(loadingKey);

    const filterMap: Record<string, string> = {};
    Object.entries(selectedPromptOptionValues).forEach(([option, value]) => {
      filterMap[option] = value as string;
    });

    const includeImages = editableComponentsMap[EditableComponent.IMAGE] ?? false;
    const includeText = editableComponentsMap[EditableComponent.TEXT] ?? false;
    const includeStyles = editableComponentsMap[EditableComponent.STYLES] ?? false;

    const morphResponse = await morphPoster(hashedId, prompt, filterMap, includeImages, includeText, includeStyles);
    morphId = morphResponse?.morphId;

    await refreshTeamCreditsInStore();
    setPromptBoxPreviousPrompt(prompt);
  } finally {
    terminatePosterMorphLoading(loadingKey);
    if (morphId) {
      handleAiRedesignFeedback(morphId);
      trackGA4CustomEvent(GA4EventName.EDITOR_AIREWRITE_APPLIED, {[GA4EventParamName.PROMPT]: prompt});
    }
  }
};

export const onAiRedesignRecentPromptSelected = (): void => {
  trackGA4CustomEvent(GA4EventName.EDITOR_AIREWRITE_PRESET, {[GA4EventParamName.TYPE]: 'Recent'});
};

export const onAiRedesignPromptSuggestionSelected = (promptSuggestionValue: PromptSuggestionsValue): void => {
  trackGA4CustomEvent(GA4EventName.EDITOR_AIREWRITE_PRESET, {[GA4EventParamName.TYPE]: 'Preset', [GA4EventParamName.VALUE]: promptSuggestionValue});
};

export const onAiRedesignPromptOptionChanged = (promptOption: PromptOption, promptOptionValue: PromptOptionValue): void => {
  switch (promptOption) {
    case PromptOption.MOOD:
      trackGA4CustomEvent(GA4EventName.EDITOR_AIREWRITE_MOOD, {mood: promptOptionValue as string});
      break;
    case PromptOption.EMOJI_USAGE:
      trackGA4CustomEvent(GA4EventName.EDITOR_AIREWRITE_EMOJIS, {emojiUsage: promptOptionValue as string});
      break;
    default:
      break;
  }
};

export const onAiRedesignIconClicked = (): void => {
  trackGA4CustomEvent(GA4EventName.EDITOR_AIREWRITE_CLICK);
};

export const onAiRedesignContextualMenuOpened = (): void => {
  trackGA4CustomEvent(GA4EventName.EDITOR_AIREWRITE_SETTINGS);
};

async function submitAiRedesignFeedbackReaction(morphId: string, reaction: FeedbackReaction): Promise<void> {
  await window.PMW.writeLocal('posterdesignmorph/recordAiRedesignFeedbackReaction', {
    morphId,
    reaction,
  });
}

async function submitAiRedesignFeedbackComment(morphId: string, feedbackText: string): Promise<void> {
  await window.PMW.writeLocal('posterdesignmorph/recordAiRedesignFeedbackComment', {
    morphId,
    comment: feedbackText,
  });
}

export const handleAiRedesignFeedback = (morphId: string): void => {
  const submitFeedbackReaction = async (reaction: FeedbackReaction): Promise<void> => {
    await submitAiRedesignFeedbackReaction(morphId, reaction);
  };

  const submitFeedbackTextInput = async (feedbackText: string): Promise<void> => {
    await submitAiRedesignFeedbackComment(morphId, feedbackText);
  };

  window.PMW.showFeedbackToast({
    onFeedbackReaction: submitFeedbackReaction,
    onFeedbackTextInput: submitFeedbackTextInput,
    reactionPageHeaderTitle: window.i18next.t('pmwjs_ai_just_worked_its_magic'),
    reactionPageHeaderIcon: 'icon-check',
    feedbackInputHeaderTitle: window.i18next.t('pmwjs_help_us_improve'),
    feedbackInputHeaderIcon: 'icon-message',
    feedbackInputPlaceholder: window.i18next.t('pmwjs_explain_your_experience_ellipsis'),
    postSubmissionText: window.i18next.t('pmwjs_thank_you_for_your_feedback'),
  });
};

export const saveEditableComponentsMapToStorage = (editableComponentsMap: EditableComponentsMap): void => {
  try {
    localStorage.setItem(POSTER_AI_REDESIGN_EDITABLE_COMPONENTS_KEY, JSON.stringify(editableComponentsMap));
  } catch (error) {
    window.PMW.log('Failed to save editable components map to localStorage', error);
  }
};

export const loadEditableComponentsMapFromStorage = (): EditableComponentsMap => {
  try {
    const stored = localStorage.getItem(POSTER_AI_REDESIGN_EDITABLE_COMPONENTS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as EditableComponentsMap;

      return {
        ...DEFAULT_EDITABLE_COMPONENTS_MAP,
        ...parsed,
      };
    }
  } catch (error) {
    window.PMW.log('Failed to load editable components map from localStorage', error);
  }

  return DEFAULT_EDITABLE_COMPONENTS_MAP;
};

export const getEditableComponentsMap = (): EditableComponentsMap => {
  return loadEditableComponentsMapFromStorage();
};

export const getPromptOptionsForEditableComponents = (editableComponentsMap: EditableComponentsMap): PromptOptionsForEditableComponents => {
  const promptOptions: PromptOptionsForEditableComponents = {};

  if (editableComponentsMap[EditableComponent.TEXT]) {
    promptOptions[EditableComponent.TEXT] = [PromptOption.MOOD, PromptOption.EMOJI_USAGE];
  }

  if (editableComponentsMap[EditableComponent.IMAGE]) {
    promptOptions[EditableComponent.IMAGE] = [PromptOption.AI_MODEL];
  }

  return promptOptions;
};

export const getHeaderContent = (): HeaderContent => {
  return {
    title: window.i18next.t('pmwjs_update_text_with_ai'),
    showPulsatingDot: true,
  };
};

export function getPromptBoxPreviousPrompt(): string | undefined {
  const storedPrompt = sessionStorage.getItem(POSTER_AI_REDESIGN_PREVIOUS_PROMPT);

  if (storedPrompt) {
    return JSON.parse(storedPrompt) as string;
  }

  return undefined;
}

export function setPromptBoxPreviousPrompt(prompt: string): void {
  if (prompt?.trim()) {
    sessionStorage.setItem(POSTER_AI_REDESIGN_PREVIOUS_PROMPT, JSON.stringify(prompt));
  }
}

export const createPosterEditorAiRedesignModalProps = (teamCredits?: number): AiRedesignPromptBoxPanel => {
  const currentEditableComponentsMap = getEditableComponentsMap();
  const redesignCost = calculateAiRedesignCost(currentEditableComponentsMap);
  const remainingCredits = teamCredits ?? 0;

  const handleRegenerateContent = async (prompt: string, selectedPromptOptionValues: SelectedPromptOptionValues, editableComponentsMap?: EditableComponentsMap): Promise<void> => {
    const componentsMap = editableComponentsMap ?? getEditableComponentsMap();
    await regenerateContent(prompt, selectedPromptOptionValues, componentsMap, remainingCredits);
  };

  const onEditableComponentsMapChange = (editableComponentsMap: EditableComponentsMap): void => {
    saveEditableComponentsMapToStorage(editableComponentsMap);
  };

  return {
    containerClasses: 'spacing-p-3',
    title: window.i18next.t('pmwjs_update_text_with_ai'),
    icon: '',
    showRefreshSuggestions: true,
    moodType: MoodType.REWRITE,
    regenerateContent: handleRegenerateContent,
    placeholder: window.i18next.t('pmwjs_rewrite_this_for_my_coffee'),
    editableComponentsMap: currentEditableComponentsMap,
    promptOptionsForEditableComponents: getPromptOptionsForEditableComponents(currentEditableComponentsMap),
    promptSuggestions: PromptSuggestions.DESIGN,
    recentPrompt: getPromptBoxPreviousPrompt,
    onPromptChange: setPromptBoxPreviousPrompt,
    creditsPillContent: {
      remainingCredits,
      actionCost: redesignCost,
    },
    onEditableComponentsMapChange,
    unselectableEditableComponents: {
      [EditableComponent.IMAGE]: window.i18next.t('pmwjs_coming_soon_exclamation'),
      [EditableComponent.TEXT]: window.i18next.t('pmwjs_required'),
    },
    upsellDialog: {
      dialogName: window.PMW.PREMIUM_ONLY_FEATURE_NAME_DESIGN_EDITOR_AI_REWRITE as string,
      showDialog: !isUserPremium(),
    },
    onStartingIconClicked: onAiRedesignIconClicked,
    onContextualMenuOpened: onAiRedesignContextualMenuOpened,
    onPromptOptionChanged: onAiRedesignPromptOptionChanged,
  };
};

export const openPosterEditorAiRedesignModal = (teamCredits?: number | null): void => {
  const props = createPosterEditorAiRedesignModalProps(teamCredits ?? undefined);
  window.PMW.redux.store.dispatch(
    openPanelInModal({
      modalId: MODALS_IDS.AI_REDESIGN_PROMPT_BOX_MODAL,
      panelProps: props,
    })
  );
};
